import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router";

import { ChevronLeft } from "@/assets/icons/ds";
import { ChevronRight } from "@/assets/icons/icons";

import { getPages } from "./paginationCalculations";

interface Props {
  paginationPages: number;
}

/**
 * Base tile geometry and typography shared by every page number and arrow.
 * State-specific colours (active/hover/pressed/focus) are layered on top per
 * element via {@link INTERACTIVE_CLASS} and {@link FOCUS_CLASS}.
 */
const ITEM_CLASS =
  "flex size-8 shrink-0 items-center justify-center rounded-md text-body-base font-semibold leading-none";

/**
 * Keyboard focus ring for interactive tiles, per the Figma spec: a 2px ring
 * offset 4px outside the tile with an 8px corner radius (rounder than the
 * tile's own 4px radius). Drawn via `after:` since a plain `outline` can't
 * have a radius independent of the element's own border-radius.
 */
const FOCUS_CLASS =
  "relative outline-none after:pointer-events-none after:absolute after:inset-[-4px] after:rounded-lg after:border-2 after:border-transparent after:content-[''] focus-visible:after:border-border-primary";

/**
 * Hover, pressed and focus background for tiles that are clickable but not the
 * active page (inactive page numbers and both arrows). All three states share
 * the same translucent fill.
 */
const INTERACTIVE_CLASS =
  "text-foreground-primary hover:bg-surface-secondary active:bg-surface-secondary focus-visible:bg-surface-secondary";

export const Pagination = ({ paginationPages }: Props) => {
  const { t } = useTranslation(["portal", "translation"]);
  const paramName = "page";

  const location = useLocation();
  // Use search params to control pagination
  const [searchParams] = useSearchParams();
  // The currently active param is the value for the search param with paramName
  const currentPage = Number(searchParams.get(paramName)) || 1;
  // Transform our searchParams to a native URLSearchParams object
  const nativeSearchParams = new URLSearchParams(searchParams.toString());
  const getPaginationTarget = (value: number) => {
    nativeSearchParams.set(paramName, value.toString());

    return location.pathname + "?" + nativeSearchParams.toString();
  };

  const pageNumbers = getPages(currentPage, paginationPages);
  const lastElement = pageNumbers[pageNumbers.length - 1];

  return (
    // Mobile shows the same seven page tiles as the design, which only fit a
    // 360px card without the 4px gaps; overflow-x is a safety valve so an even
    // narrower phone scrolls the strip instead of breaking the card.
    // `p-1`: FOCUS_CLASS draws its ring 4px outside a tile's own box - without
    // this, `overflow-x-auto` (which also computes overflow-y to `auto` per
    // the CSS overflow spec, since only one axis was set) clips that ring on
    // whichever tile sits flush against an edge. Reset at `tablet`, where
    // `overflow-visible` means nothing is clipped, so the padding would only
    // add unnecessary space.
    <nav
      className="flex items-center justify-center gap-0 overflow-x-auto p-1 [-ms-overflow-style:none] [scrollbar-width:none] tablet:gap-1 tablet:overflow-visible tablet:p-0 [&::-webkit-scrollbar]:hidden"
      aria-label={t("layout.table.pagination.ariaLabel")}
    >
      {currentPage === 1 ? (
        <span
          className={clsx(ITEM_CLASS, "text-foreground-tertiary")}
          aria-hidden="true"
        >
          <ChevronLeft className="size-4" />
        </span>
      ) : (
        <Link
          className={clsx(ITEM_CLASS, FOCUS_CLASS, INTERACTIVE_CLASS)}
          to={getPaginationTarget(currentPage - 1)}
          aria-label={t("translation:Common.label.back")}
        >
          <ChevronLeft className="size-4" />
        </Link>
      )}
      {pageNumbers.map((pageNumber, index) =>
        pageNumber === -1 ? (
          <span
            key={`placeholder-${index}`}
            data-testid="pagination-placeholder"
            className={clsx(ITEM_CLASS, "text-foreground-primary")}
          >
            ...
          </span>
        ) : (
          <Link
            key={pageNumber}
            data-testid={`pagination-${pageNumber}`}
            className={clsx(
              ITEM_CLASS,
              FOCUS_CLASS,
              `${currentPage}` === `${pageNumber}`
                ? "bg-surface-primary text-foreground-primary-inverse"
                : INTERACTIVE_CLASS,
            )}
            to={getPaginationTarget(pageNumber)}
            // The filled tile is the only visual cue for the active page.
            aria-current={
              `${currentPage}` === `${pageNumber}` ? "page" : undefined
            }
          >
            {pageNumber}
          </Link>
        ),
      )}
      {currentPage === lastElement ? (
        <span
          className={clsx(ITEM_CLASS, "text-foreground-tertiary")}
          aria-hidden="true"
        >
          <ChevronRight className="size-4" />
        </span>
      ) : (
        <Link
          className={clsx(ITEM_CLASS, FOCUS_CLASS, INTERACTIVE_CLASS)}
          to={getPaginationTarget(currentPage + 1)}
          aria-label={t("translation:Common.label.forward")}
        >
          <ChevronRight className="size-4" />
        </Link>
      )}
    </nav>
  );
};
